'use client';

import { useId } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { KitchenSkin } from '../functional/withKitchenSkin';
import { SkLink, text, type Localized } from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

export interface SpatialKitchenCatalogueProps {
  title?: Localized;
  intro?: Localized;
  categorySlugPath?: string;
  brandSlug?: string;
  searchPlaceholder?: Localized;
  anchorId?: string;
}

const CSS = `
.sk-shop{padding-block:2rem 3.5rem;scroll-margin-top:6rem}
.sk-shop__head{display:grid;gap:.75rem;margin-bottom:1.5rem;max-width:48rem}
.sk-shop__layout{display:grid;gap:1.5rem;align-items:start}
.sk-shop__filters{border:1px solid var(--sk-border);border-radius:8px;background:var(--sk-warm);min-width:0}
.sk-shop__filters summary{padding:1rem;font-weight:500;cursor:pointer;min-height:48px}
.sk-shop__results{min-width:0}
.sk-shop .sk-skin{padding:0}
.sk-shop .sk-skin__inner{padding:0;max-width:none}
.sk-shop__filters .sk-skin__inner{padding:0 1rem 1rem}
.sk-shop__help{display:flex;flex-wrap:wrap;gap:.75rem 1.5rem;margin-top:1.5rem;border-top:1px solid var(--sk-border);padding-top:1.25rem;font-size:.9375rem}
@media(min-width:1024px){.sk-shop__layout{grid-template-columns:15rem minmax(0,1fr);gap:2rem}}
`;

/** Both canonical blocks receive exactly the same scope and share URL filter state. */
export function SpatialKitchenCatalogue(props: SpatialKitchenCatalogueProps) {
  const headingId = useId();
  const scope = { categorySlugPath: props.categorySlugPath, brandSlug: props.brandSlug };
  return (
    <BlockRoot as="section" id={props.anchorId || 'products'} className="sk sk-shop" aria-labelledby={headingId}>
      <SkBaseStyles />
      <SkStyles id="shop" css={CSS} />
      <div className="sk-container">
        <div className="sk-shop__head">
          <h2 id={headingId} className="sk-heading sk-h2">{text(props.title, 'Shop the range')}</h2>
          {text(props.intro) && <p className="sk-body">{text(props.intro)}</p>}
        </div>
        <div className="sk-shop__layout">
          <details className="sk-shop__filters">
            <summary>Filter products</summary>
            <KitchenSkin skinType="FacetPanel" skinWidth="wide" blockProps={{ ...scope, title: 'Refine your choice', showPriceRange: true, showInStockOnly: false, defaultExpanded: true }} />
          </details>
          <div className="sk-shop__results">
            <KitchenSkin skinType="ProductGrid" skinWidth="wide" blockProps={{ ...scope, columns: 'auto', pageSize: 24, showSearch: true, searchPlaceholder: text(props.searchPlaceholder, 'Search this range'), showSort: true, showResultCount: true, emptyTitle: 'No products match these filters — try another size or clear your filters.' }} />
          </div>
        </div>
        <div className="sk-shop__help">
          <SkLink href="/kitchen-measuring-guide">Help with measuring</SkLink>
          <SkLink href="/contact">Cannot find what you need? Ask us</SkLink>
          <SkLink href="/delivery">Delivery information</SkLink>
        </div>
      </div>
    </BlockRoot>
  );
}
